import { config } from "@/config";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

export const sentry = {
  init: () =>
    config.mode === "emulator"
      ? null
      : Sentry.init({
          dsn: config.sentryDsn,
          environment: config.mode,
          release: `${config.version}-${config.commitHash}`,
          integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
              useEffect: React.useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
            Sentry.replayIntegration(),
            Sentry.browserProfilingIntegration(),
          ],
          // Performance Monitoring
          tracesSampleRate: config.mode === "prod" ? 0.3 : 1.0, //  Capture 100% of the transactions
          // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
          tracePropagationTargets: [
            "localhost",
            /^https:\/\/shopherodashboard\.web\.app/,
            /^https:\/\/shophero-dashboard-staging\.web\.app/,
            /^https:\/\/dev-shdb\.web\.app/,
          ],
          // Session Replay
          replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
          replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
          // Set profilesSampleRate to 1.0 to profile every transaction.
          // Since profilesSampleRate is relative to tracesSampleRate,
          // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
          // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
          // results in 25% of transactions being profiled (0.5*0.5=0.25)
          profilesSampleRate: config.mode === "prod" ? 0.1 : 1.0,
          beforeSendSpan: (span) => {
            span.data = {
              ...span.data,
              "app.version": config.version,
              "app.hash": config.commitHash,
              "app.environment": config.mode,
            };

            return span;
          },
        }),
  setUser: ({
    email,
    id,
    shopId,
  }: {
    email?: string;
    id?: string;
    shopId?: string;
  }) => {
    if (!email || !id) {
      Sentry.setUser(null);
    }
    Sentry.setUser({ email, id, shopId: shopId ?? "" });
  },
  captureException: Sentry.captureException,
  wrapCreateBrowserRouterV6: Sentry.wrapCreateBrowserRouterV6,
  withProfiler: Sentry.withProfiler,
  startSpan: Sentry.startSpan,
};

export const captureException = Sentry.captureException;
